import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'Douleutaras/components/Modal/Modal';
import ModalSimplePanel from 'Douleutaras/components/modals/ModalSimplePanel';
import { Accordion } from 'Douleutaras/components/Accordion/Accordion';

import './ServiceInfoModal.scss';

/** One titled block of info copy. `html` is backend-sanitized, already-localized rich text. */
export interface ServiceInfoSection {
  heading: string;
  html: string;
}

interface ServiceInfoModalProps {
  isOpen: boolean;
  /** Modal title — the label of the node the ⓘ was opened from. */
  title: string;
  sections: Array<ServiceInfoSection>;
  /**
   * `accordion` collapses each section behind its heading (category level); `plain` stacks
   * headings and copy in the flow (service and detail level).
   */
  layout?: 'accordion' | 'plain';
  /** Appends the indicative-price / ex-VAT note under the sections. */
  priceNote?: boolean;
  onClose(): void;
}

/**
 * The single info modal behind every ⓘ in the service catalogue. Content comes from the
 * pricing payload (#5613) as sanitized rich text and is rendered as returned — never
 * translated, reformatted, or re-sanitized here.
 */
export const ServiceInfoModal = ({
  isOpen,
  title,
  sections,
  layout = 'plain',
  priceNote = false,
  onClose
}: ServiceInfoModalProps) => {
  const { t } = useTranslation();
  const filledSections = sections.filter(section => Boolean(section.html));
  const isAccordion = layout === 'accordion';

  if (!isOpen || filledSections.length === 0) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  return (
    // Default panel box: full-screen on mobile, fixed size from tabletBig up
    <Modal isOpen={isOpen} dismissCallback={onClose}>
      <ModalSimplePanel showHead noPadding title={title} closeCallback={onClose}>
        <div styleName="service-info-modal">
          {isAccordion
            ? filledSections.map((section, index) => (
                <Accordion
                  key={section.heading}
                  arrowPosition="left"
                  title={section.heading}
                  open={index === 0}
                  modification="compact"
                >
                  {/* Sanitized server-side by #5613 (bleach, iframe/script stripped) */}
                  <div
                    styleName="service-info-modal__rich-text"
                    dangerouslySetInnerHTML={{ __html: section.html }}
                  />
                </Accordion>
              ))
            : (
              <div styleName="service-info-modal__sections">
                {filledSections.map(section => (
                  <section key={section.heading} styleName="service-info-modal__section">
                    <h3 styleName="service-info-modal__heading">{section.heading}</h3>
                    {/* Sanitized server-side by #5613 (bleach, iframe/script stripped) */}
                    <div
                      styleName="service-info-modal__rich-text"
                      dangerouslySetInnerHTML={{ __html: section.html }}
                    />
                  </section>
                ))}
              </div>
            )}

          {priceNote && (
            <p styleName="service-info-modal__note">
              <span
                className="dashicon dashicon-light-bulb"
                styleName="service-info-modal__note-icon"
                aria-hidden="true"
              />
              <span>
                {t(
                  'T.PROS-PROFILE.SERVICES.INFO-MODAL-PRICE-NOTE',
                  'Prices are indicative and exclude VAT. The final price is set after the professional’s assessment.'
                )}
              </span>
            </p>
          )}
        </div>
      </ModalSimplePanel>
    </Modal>
  );
};

export default ServiceInfoModal;
